import * as THREE from "three";

export const DEFAULT_TEXTURE_WIDTH = 2048;

function configureTexture(texture) {
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  texture.needsUpdate = true;

  return texture;
}

function createDataTexture(data, width, height, type) {
  return configureTexture(
    new THREE.DataTexture(data, width, height, THREE.RGBAFormat, type),
  );
}

export function getGaussianTextureDimensions(
  count,
  maxTextureSize,
  preferredWidth = DEFAULT_TEXTURE_WIDTH,
) {
  if (!Number.isInteger(count) || count <= 0) {
    throw new Error(`Invalid Gaussian texture count: ${count}`);
  }

  if (!Number.isInteger(maxTextureSize) || maxTextureSize <= 0) {
    throw new Error(`Invalid maximum texture size: ${maxTextureSize}`);
  }

  const width = Math.min(preferredWidth, maxTextureSize, count);

  const height = Math.ceil(count / width);

  if (height > maxTextureSize) {
    throw new Error("Gaussian dataset exceeds GPU texture dimensions.");
  }

  return { width, height };
}

function createFloatTexture(
  source,
  components,
  count,
  width,
  height,
  defaultAlpha = 0,
) {
  const data = new Float32Array(width * height * 4);

  for (let i = 0; i < count; i += 1) {
    const sourceOffset = i * components;
    const textureOffset = i * 4;

    data[textureOffset] = source[sourceOffset] ?? 0;

    data[textureOffset + 1] =
      components > 1 ? (source[sourceOffset + 1] ?? 0) : 0;

    data[textureOffset + 2] =
      components > 2 ? (source[sourceOffset + 2] ?? 0) : 0;

    data[textureOffset + 3] =
      components > 3
        ? (source[sourceOffset + 3] ?? defaultAlpha)
        : defaultAlpha;
  }

  return createDataTexture(data, width, height, THREE.FloatType);
}

function createColorTexture(colors, count, width, height) {
  const data = new Uint8Array(width * height * 4);

  for (let i = 0; i < count; i += 1) {
    const sourceOffset = i * 3;
    const textureOffset = i * 4;

    data[textureOffset] = Math.round(
      THREE.MathUtils.clamp(colors[sourceOffset] ?? 0, 0, 1) * 255,
    );

    data[textureOffset + 1] = Math.round(
      THREE.MathUtils.clamp(colors[sourceOffset + 1] ?? 0, 0, 1) * 255,
    );

    data[textureOffset + 2] = Math.round(
      THREE.MathUtils.clamp(colors[sourceOffset + 2] ?? 0, 0, 1) * 255,
    );

    data[textureOffset + 3] = 255;
  }

  return createDataTexture(data, width, height, THREE.UnsignedByteType);
}

function createCenterOpacityTexture(data, count, width, height) {
  const textureData = new Float32Array(width * height * 4);

  for (let i = 0; i < count; i += 1) {
    const positionOffset = i * 3;
    const textureOffset = i * 4;

    textureData[textureOffset] = data.positions[positionOffset];

    textureData[textureOffset + 1] = data.positions[positionOffset + 1];

    textureData[textureOffset + 2] = data.positions[positionOffset + 2];

    textureData[textureOffset + 3] = data.opacities[i];
  }

  return createDataTexture(textureData, width, height, THREE.FloatType);
}

export function createGaussianTextures(
  data,
  count,
  maxTextureSize,
  preferredWidth = DEFAULT_TEXTURE_WIDTH,
) {
  if (!data || count <= 0) {
    return null;
  }

  const { width, height } = getGaussianTextureDimensions(
    count,
    maxTextureSize,
    preferredWidth,
  );

  return {
    centerOpacity: createCenterOpacityTexture(data, count, width, height),

    scale: createFloatTexture(data.scales, 3, count, width, height),

    rotation: createFloatTexture(data.rotations, 4, count, width, height),

    color: createColorTexture(data.colors, count, width, height),

    width,
    height,
  };
}

export function disposeGaussianTextures(textures) {
  if (!textures) {
    return;
  }

  textures.centerOpacity?.dispose();
  textures.scale?.dispose();
  textures.rotation?.dispose();
  textures.color?.dispose();
}
